import {
    getAttachmentUrl,
    getMessage,
    getMessageBody,
    listMessages
} from './queries';
import {
    createMessage,
    deleteMessage,
    updateMessage,
    sendEmail as sendEmailMutation,
} from './mutations';

import { GraphQLQuery } from 'aws-amplify/api';
import {
    CreateMessage,
    DeleteMessage,
    GetAttachmentUrl,
    GetMessage,
    GetMessageBody,
    ListMessages,
    Message,
    UpdateMessage
} from "../types.ts";
import {
    showErrorNotifications,
    showSuccessNotification
} from "../../../components/ServiceNotifications.tsx";
import {config} from "../config";
import { client } from '../../email-api-integration';


export const fetchRecords = async (
    {
        aliasId,
        tagId,
        status,
        limit = 10,
        nextToken = null
    }: {
        aliasId?: string
        tagId?: string,
        status?: string,
        limit?: number,
        nextToken?: string | null
    }) => {

    let filter = {}

    if (typeof aliasId !== 'undefined' && aliasId !== null) {
        filter = {
            ...filter,
            ...{aliasId: aliasId}
        };
    }

    if (typeof tagId !== 'undefined' && tagId !== null) {
        filter = {
            ...filter,
            ...{tagIds: [tagId]}
        }
    }

    if (typeof status !== 'undefined' && status !== null) {
        filter = {
            ...filter,
            ...{status: status}
        };
    }

    console.debug('IN FETCH RECORDS. FILTER', filter);

    try {
        const response = await client.graphql<GraphQLQuery<ListMessages>>({
                                                                              query: listMessages,
                                                                              variables: {
                                                                                  filter: filter,
                                                                                  limit: limit,
                                                                                  nextToken: nextToken
                                                                              },
                                                                          });
        const result = {
            items: response.data?.listMessages.items || [],
            nextToken: response.data?.listMessages.nextToken || null
        };
        console.log('fetchRecords returning:', { itemsCount: result.items.length, nextToken: result.nextToken });
        
        return result;
    } catch (e) {
        console.trace(e);
        return { items: [], nextToken: null };
    }
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetMessage>>({
                                                                        query: getMessage,
                                                                        variables: {id: id},
                                                                    });
    return response.data?.getMessage;
};

/**
 * FNP-1532: read a message body through the API.
 *
 * Replaces `downloadData` against S3. The server picks the artifact
 * (text -> parsed -> html, skipping empty ones) and enforces alias ownership, so
 * the caller neither chooses nor sees an S3 key.
 *
 * Returns `contentType` — which artifact was actually served. A caller that needs
 * plain text MUST branch on it; an HTML-only message yields markup.
 */
export const fetchBody = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetMessageBody>>({
        query: getMessageBody,
        variables: {id},
    });
    return response.data?.getMessageBody ?? null;
};

/**
 * FNP-1532: mint a short-lived download URL for one attachment.
 *
 * `index` is the position in the message's own `attachments` array. Call this ON
 * CLICK — minting for every row of a list turns one render into N signatures.
 */
export const fetchAttachmentUrl = async (messageId: string, index: number) => {
    const response = await client.graphql<GraphQLQuery<GetAttachmentUrl>>({
        query: getAttachmentUrl,
        variables: {messageId, index},
    });
    return response.data?.getAttachmentUrl ?? null;
};

export const add = async (payload: object) => {

    try {
        const response = await client.graphql<GraphQLQuery<CreateMessage>>({
                                                                               query: createMessage,
                                                                               variables: {input: payload},
                                                                           });
        console.debug('in the service response', response);

        showSuccessNotification(`${config.titles.singular} created`);
        return response.data?.createMessage;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const update = async (id: string, record: Message) => {

    console.debug('IN UPDATE', id, record);

    delete record.created;
    delete record.updated;
    delete record.tags;
    delete record.createdByUsername;
    delete record.managedByUsername;

    try {
        const response = await client.graphql<GraphQLQuery<UpdateMessage>>({
                                                                               query: (updateMessage),
                                                                               variables: {input: record},
                                                                           });

        showSuccessNotification(`${config.titles.singular} updated`);
        return response.data?.updateMessage;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<DeleteMessage>>({
                                                                               query: (deleteMessage),
                                                                               variables: {input: {id: id}},

                                                                           });
        showSuccessNotification(`${config.titles.singular} deleted`);
        return response.data?.deleteMessage;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const sendEmail = async (input: {
    aliasId: string;
    to: string[];
    subject: string;
    htmlBody?: string;
    textBody?: string;
    inReplyTo?: string;
}) => {
    try {
        const response = await client.graphql({
            query: sendEmailMutation,
            variables: { input },
        });

        const result = (response as any).data?.sendEmail;
        if (result) {
            showSuccessNotification('Email sent successfully');
        } else {
            showSuccessNotification('Email queued for sending');
        }
        return result;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e);
    }
};
